import Vue from 'vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import zenscroll from 'zenscroll';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    checkOneTrustConsentByWindowChange,
    normalizeModelName,
} from '../utils';

gsap.registerPlugin(ScrollTrigger);

Vue.component('configurator', {
    props: {
        siteHandle: {
            type: String,
        },
        model: {
            type: String,
        },
        initialDisclaimer: {
            type: String,
        },
        initialVersionDisclaimer: {
            type: String,
        },
        googleMapsApiKey: {
            type: String,
        },
        isAnalyticsEnabled: {
            type: String,
        },
        modelSpecs: {
            type: String,
        },
    },

    components: {
        'configurator-payment-options': () =>
            import('../async-components/async-configurator-payment-options'),
        'santander-modal-component': () =>
            import('../async-components/async-santander-modal-component'),
        'scf-modal-component': () =>
            import('../async-components/async-scf-modal-component'),
        'configurator-stock': () =>
            import('../async-components/async-configurator-stock'),
    },

    data() {
        return {
            configurationCompleted: false,
            disclaimer: '',
            versionDisclaimer: '',
            scfCalculations: {
                materialCode: '',
                totalPriceNumber: '',
                promotionPriceMetallic: 0,
                promotionPriceNonMetallic: 0,
            },
            configuratorResult: {
                color: '',
                type: '',
                options: {},
                promotions: {},
                version: {},
                price: '',
                totalPriceIndication: '',
                priceIndication: '',
                disclaimer: '',
            },
            edition: null,
            carEditionId: null,
            selectCEECountry: false,
            hasInteriorImage: true,
            countyPriceMapping: {
                'be-FR': {
                    localization: 'nl-NL',
                    showZeros: false,
                },
                'fr-FR': {
                    localization: 'nl-NL',
                    showZeros: false,
                },
                'is-IS': {
                    localization: 'nl-NL',
                    showZeros: false,
                },
                'de-DE': {
                    localization: 'de-DE',
                    showZeros: true,
                },
                'de-AT': {
                    localization: 'de-AT',
                    showZeros: true,
                },
            },
            vehicleName: '',
            vehicleCode: null,

            // Santander calculation
            typeOfFinancing: null,
            deposit: null,
            duration: null,
            installmentAmount: null,
            isSantanderCalculation: false,
            showStockResult: false,
            brandstoreStock: null,
            geocoder: null,
            isBeluxChargeSolution: null,

            // FR finance calculator
            agricoleOriginalPrice: 0,
            agricoleEcommercePrice: 0,

            // Specs visibility
            specsVisible: false,

            // Chapter functionality
            chapterOpen: false,
            scrollToElement: null,

            scfResultCalculations: null,
        };
    },

    watch: {
        /**
         * Track & send results and switch to dark mode if configuration has been completed, or go back.
         *
         * @param {Boolean} value
         */
        configurationCompleted(value) {
            if (value) {
                this.trackConfiguration();
                EventBus.$emit(
                    'configurator-completed',
                    this.configuratorResult,
                );
                this.$root.darkColorSchemeSwitch = true;
                return;
            }
            this.$root.darkColorSchemeSwitch = false;
        },

        carEditionId: function (carEditionIdValue) {
            if (!carEditionIdValue) return;
            if (!checkCCM19Consent()) return;
            this.handleSetStartConfigurationDataLayer();
        },
    },

    created() {
        // Handle main disclaimer text.
        this.handleMainDisclaimer();

        // Handle version disclaimer text.
        this.handleVersionDisclaimer();

        // Listen to the events of children to update the result.
        this.updateResultOnChildEvents();

        // Set configurator uncompleted on event.
        EventBus.$on('configurator-back-to-the-studio', () => {
            this.showStockResult = false;
            this.configurationCompleted = false;
        });

        EventBus.$on('configurator-remove-stock-code', (data) => {
            if (data) this.vehicleCode = null;
        });

        EventBus.$on('complete-santander-configuration', (data) => {
            this.completeConfigurator();
            this.completeSantanderCalculation(data);
        });

        EventBus.$on('openSpecs', () => {
            this.specsSelectedModelName = this.modelName;
            this.selectedSpecsModelType = '';
            this.selectedSpecsModelSubline = '';
            this.selectedSpecsModelSublineAsset = null;
            document.body.style.overflow = 'hidden';
        });

        EventBus.$on('closeSpecs', () => {
            this.chapterOpen = false;
        });

        EventBus.$on('scf-modal-confirm', (data) => {
            this.scfResultCalculations = data;
            this.completeConfigurator();
        });
    },

    mounted() {
        this.injectGoogleMaps();

        setTimeout(() => {
            if (
                !checkOneTrustConsentByWindowChange(window.OnetrustActiveGroups)
            ) {
                return;
            }
            this.handleSetStartConfigurationDataLayer();
        }, [1000]);

        EventBus.$once('google-maps-ready', this.initMap);
        EventBus.$emit('initial-configurator-created', this.configuratorResult);

        if (!JSON.parse(this.modelSpecs)) return;
        this.initSpecsClickHandler();
    },

    computed: {
        isChargeSolutionBelux() {
            const isBeluxCountries =
                this.siteHandle === 'mgMotorsBelgiumNl' ||
                this.siteHandle === 'mgMotorsBelgiumFr';
            const isPetrolCar = this.model === 'hs' || this.model === 'zs';

            if (!isBeluxCountries || (isBeluxCountries && isPetrolCar)) {
                return false;
            }

            return true;
        },

        toggleChapterClass() {
            return this.chapterOpen ? 'section-chapter--open' : '';
        },

        toggleModelSpecsClass() {
            return this.chapterOpen
                ? 'section-chapter--open model-new-section-chapter--custom-spacing'
                : '';
        },
    },

    methods: {
        handleModalVisiblity() {
            this.showStockResult = !this.showStockResult;
        },

        async completeConfiguratorStock(data) {
            this.showConfiguratorStockResult(data);

            await this.$nextTick();

            document
                .getElementById('configurator-stock-result')
                .scrollIntoView({ behavior: 'smooth' });
        },

        handleGetSantanderCode(edition) {
            const santanderItalyEurotaxCode = Object.keys(edition).find((key) =>
                key.includes('santanderItalyEurotaxCode'),
            );
            const isSantanderItalyEurotaxCodeEmpty =
                !santanderItalyEurotaxCode ||
                !Boolean(santanderItalyEurotaxCode.length);

            if (isSantanderItalyEurotaxCodeEmpty) return null;

            return edition[santanderItalyEurotaxCode];
        },

        injectGoogleMaps() {
            var tag = document.createElement('script');
            tag.setAttribute('type', 'text/javascript');
            tag.defer = true;
            tag.async = true;
            tag.setAttribute(
                'src',
                `//maps.google.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=places&callback=mapsCallback`,
            );
            (
                document.getElementsByTagName('head')[0] ||
                document.documentElement
            ).appendChild(tag);
        },

        initMap() {
            // Adding Google maps Geocoder
            this.geocoder = new google.maps.Geocoder();
            EventBus.$emit('google-maps-ready-children', this.geocoder);
        },

        /**
         * Sets main disclaimer and updates it on payment option change if required.
         *
         * @returns {Void}
         */
        handleMainDisclaimer() {
            // Set initial disclaimer.
            this.disclaimer = this.initialDisclaimer;

            // Update main disclaimer if an option has been selected that wants to overrule it.
            EventBus.$on('configurator-set-payment-option', (paymentOption) => {
                this.disclaimer = this.initialDisclaimer;
                if (paymentOption.field_paymentOption_alternateDisclaimer) {
                    this.disclaimer =
                        paymentOption.field_paymentOption_alternateDisclaimer;
                }
            });
        },

        // This is here because CraftCMS creates ternary operator that checks if cached file exists.
        // This happens when you add a link to a file on a CDN that's on the same environment as the website.
        // This logic probably shouldn't even exist here because twig should handle this on it's own.
        // But since we're creating a logic where everything is parsed and showed through Vue, which I think is a bad idea in general,
        // I think that this should be parsed on the backend and then passed to Vue.
        // For now this is the best solution I could come up with.
        parseVersionDisclaimer(disclaimer) {
            if (!disclaimer?.length) return;

            const assetRegex = /\{asset:\d+:url\|\|([^}]+)\}/g;
            const entryRegex = /\{entry:(\d+)(@\d+)?:url\|\|([^}]+)\}/g;
            const isImage = disclaimer.includes('<img');

            if (!isImage) return disclaimer;

            return disclaimer
                .split('<img')
                .map((item) => item.replace(assetRegex, '$1'))
                .map((item) => item.replace(entryRegex, '$3'))
                .join('<img');
        },

        handleVersionDisclaimer() {
            // Set initial disclaimer.
            this.versionDisclaimer = this.initialVersionDisclaimer;

            EventBus.$on('configurator-set-disclaimer-version', (version) => {
                this.versionDisclaimer = this.initialVersionDisclaimer;
                const fieldVersionDisclaimer =
                    version?.field_version_versionDisclaimer ?? null;
                if (fieldVersionDisclaimer)
                    this.versionDisclaimer = fieldVersionDisclaimer;
            });
        },

        /**
         * Update the configuratorResult object when something changes in one of the children components.
         *
         * @returns {Void}
         */
        updateResultOnChildEvents() {
            // Fetch configurator stock brandstores
            EventBus.$on(
                'complete-configurator-stock',
                this.completeConfiguratorStock,
            );

            // Update color on color selection change.
            EventBus.$on('configurator-set-color', (color) => {
                this.configuratorResult.color = {
                    key: color.colorSlug,
                    name: color.colorTitle,
                    metallic: color.field_metalic,
                    productCode: color.colorProductCode,
                };
            });

            // Update version on version selection change.
            EventBus.$on('configurator-set-version', (version) => {
                this.configuratorResult.version = {
                    key: version?.field_version_versionHandle ?? '',
                    name: version?.field_version_versionTitle ?? '',
                    productCode: version?.field_version_productCode ?? '',
                };
                this.scfCalculations.materialCode =
                    version?.field_version_encodedMaterialCode_svaoinwu ??
                    this.getMaterialCodeFromEdition(this.edition, true);
            });

            // Update type on edition selection change.
            EventBus.$on('configurator-set-edition', (edition) => {
                this.configuratorResult.type = {
                    id: edition.id,
                    key: edition.field_commerceHandle,
                    name: edition.title,
                    productCode: edition.field_commerceProductCode,
                };

                if (this.siteHandle === 'mgMotorsItaly') {
                    this.configuratorResult.type.santanderItalyEurotaxCode =
                        this.handleGetSantanderCode(edition);
                }

                if (this.siteHandle === 'mgMotorsSpain') {
                    this.scfCalculations.materialCode =
                        this.getMaterialCodeFromEdition(edition);
                    this.edition = edition;
                }

                this.carEditionId = String(edition.id);
                this.hasInteriorImage =
                    edition.field_commerceNoInteriorImage_bjgvrydr === 0;
            });

            // Update options on option configuration change.
            EventBus.$on('configurator-set-edition-options', (options) => {
                this.options = {};
                for (let option in options) {
                    this.configuratorResult.options[
                        options[option].field_optionHandle
                    ] = {
                        name: options[option].field_optionName,
                        selected: options[option].selected,
                        shopPriceAddition: options[option].shopPriceAddition,
                        productCode: options[option].field_productCode,
                    };
                }
            });

            // Update promotions on promotion configuration change.
            EventBus.$on('configurator-set-promotions', (promotions) => {
                this.promotions = {};
                let promotionMetallicPrice = 0;
                let promotionNonMetallicPrice = 0;

                for (let promotion in promotions) {
                    // Define price depending on if there's options prices.
                    let priceImpactNonMetallic =
                        promotions[promotion].field_shopPriceImpactNonMetallic;
                    let priceImpactMetallic =
                        promotions[promotion].field_shopPriceImpactMetallic;

                    if (
                        promotions[promotion]
                            .field_useForFinancingCalculations_jxgcvanp
                    ) {
                        promotionMetallicPrice += Number(
                            promotions[promotion].field_shopPriceImpactMetallic,
                        );
                        promotionNonMetallicPrice += Number(
                            promotions[promotion]
                                .field_shopPriceImpactNonMetallic,
                        );
                    }

                    for (const [optionHandle, option] of Object.entries(
                        this.configuratorResult.options,
                    )) {
                        for (
                            let j = 0;
                            j <
                            promotions[promotion].optionsPriceAdditions.length;
                            j++
                        ) {
                            if (
                                option.selected &&
                                optionHandle ===
                                    promotions[promotion].optionsPriceAdditions[
                                        j
                                    ].field_optionPriceAddition_optionHandle
                            ) {
                                priceImpactNonMetallic =
                                    promotions[promotion].optionsPriceAdditions[
                                        j
                                    ]
                                        .field_optionPriceAddition_shopPriceImpactNonMetallic;
                                priceImpactMetallic =
                                    promotions[promotion].optionsPriceAdditions[
                                        j
                                    ]
                                        .field_optionPriceAddition_shopPriceImpactMetallic;
                            }
                        }
                    }
                    for (
                        let i = 0;
                        i < promotions[promotion].versionsPriceAdditions.length;
                        i++
                    ) {
                        if (
                            this.configuratorResult.version.key ===
                            promotions[promotion].versionsPriceAdditions[i]
                                .field_versionPriceAddition_versionHandle
                        ) {
                            priceImpactNonMetallic =
                                promotions[promotion].versionsPriceAdditions[i]
                                    .field_versionPriceAddition_shopPriceImpactNonMetallic;
                            priceImpactMetallic =
                                promotions[promotion].versionsPriceAdditions[i]
                                    .field_versionPriceAddition_shopPriceImpactMetallic;
                        }
                    }

                    this.configuratorResult.promotions[
                        promotions[promotion].field_promotionHandle
                    ] = {
                        name: promotions[promotion].field_promotionName,
                        selected: promotions[promotion].selected,
                        shopPriceImpactMetallic: priceImpactMetallic,
                        shopPriceImpactNonMetallic: priceImpactNonMetallic,
                    };
                }

                this.scfCalculations.promotionPriceMetallic =
                    promotionMetallicPrice;
                this.scfCalculations.promotionPriceNonMetallic =
                    promotionNonMetallicPrice;
            });

            // Update price on paymentOption selection change.
            EventBus.$on('configurator-set-payment-option', (paymentOption) => {
                this.configuratorResult.price = {
                    key: paymentOption.field_paymentOption_paymentHandle,
                    name: paymentOption.field_paymentOption_paymentName,
                    shopPrice: paymentOption.field_paymentOption_shopPrice,
                    shopPriceAdditionMetallic:
                        paymentOption.field_paymentOption_shopPriceAdditionMetallic,
                    shopPriceAdditionSpecificColor:
                        paymentOption.priceAdditionColor &&
                        paymentOption.priceAdditionColor[paymentOption.color]
                            ? paymentOption.priceAdditionColor[
                                  paymentOption.color
                              ].price
                            : null,
                };
            });

            // Update disclaimer on payment disclaimer change.
            EventBus.$on(
                'configurator-set-payment-disclaimer',
                (disclaimer) => {
                    this.configuratorResult.disclaimer = disclaimer;
                },
            );

            // Update totalPriceIndication on total price update.
            EventBus.$on(
                'configurator-total-price-update',
                (totalPrice, formattedTotalPrice) => {
                    this.scfCalculations.totalPriceNumber = totalPrice
                        .replace('€', '')
                        .replace('.', '')
                        .trim();
                    this.configuratorResult.totalPriceIndication =
                        formattedTotalPrice.replace(/(<([^>]+)>)/gi, ' ');
                    this.agricoleOriginalPrice = Number(
                        totalPrice.replace('.', ''),
                    );
                },
            );

            // Update priceIndication on price update.
            EventBus.$on(
                'configurator-price-update',
                (price, formattedPrice) => {
                    this.configuratorResult.priceIndication =
                        formattedPrice.replace(/(<([^>]+)>)/gi, ' ');
                    this.agricoleEcommercePrice = Number(
                        price.replace('.', ''),
                    );
                },
            );
        },

        /**
         * Handle push tu data layer for GTM when configuration is started.
         *
         * @returns {Void}
         */
        handleSetStartConfigurationDataLayer() {
            if (!window.dataLayer) {
                window.dataLayer = window.dataLayer || [];
            }

            window.dataLayer.push({
                event: 'start_configuration',
                car: {
                    name: normalizeModelName(this.model),
                    ids: [this.carEditionId],
                },
            });
        },

        showConfiguratorStockResult(data) {
            if (!data.isFinished) {
                this.showStockResult = false;
                return;
            }

            this.showStockResult = true;
            this.brandstoreStock = data.brandstores;
            this.vehicleName = data.vehicleName;
            this.vehicleCode = data.vehicleCode;
            EventBus.$emit('configurator-set-direct-payment', false);
        },

        /**
         * Trigger non-direct payment for the order while completing the configurator.
         *
         * @returns {Void}
         */
        completeConfigurator() {
            EventBus.$emit('configurator-set-direct-payment', false);
            window.scroll({
                top: 0,
            });
            this.showStockResult = false;
            this.configurationCompleted = true;
        },

        completeConfiguratorWithDealer(brandstore) {
            this.showStockResult = false;
            this.configurationCompleted = true;
            EventBus.$emit('configurator-stock-with-dealer', brandstore);
        },

        completeSantanderCalculation(data) {
            this.typeOfFinancing = data.typeOfFinancing;
            this.deposit = data.selectedDeposit;
            this.duration = data.duration;
            this.installmentAmount = data.installmentAmount;
            this.isSantanderCalculation = data.isSantanderCalculation;
        },

        /**
         * Trigger direct payment for the order while completing the configurator.
         *
         * @returns {Void}
         */
        completeConfiguratorWithDirectPayment() {
            // Trigger direct payment event.
            EventBus.$emit('configurator-set-direct-payment', true);

            // Clear existing cart if any.
            let clearCartData = new FormData();
            clearCartData.append(
                'action',
                'mg-business-logic/request-offer/clear-cart',
            );
            clearCartData.append(window.csrfTokenName, window.csrfTokenValue);
            axios
                .post('/', clearCartData)
                .then((response) => {
                    // Add product variant to cart.
                    let cartData = new FormData();
                    cartData.append('action', 'commerce/cart/update-cart');
                    cartData.append(
                        'purchasableId',
                        this.configuratorResult.type.id,
                    );
                    cartData.append(
                        window.csrfTokenName,
                        window.csrfTokenValue,
                    );
                    axios.post('/', cartData).catch((error) => {
                        console.log(error.response);
                    });
                })
                .catch((error) => {
                    console.error(error.response);
                });

            // Complete configurator.
            this.configurationCompleted = true;
            this.showStockResult = false;
        },

        /**
         * Send configuration completion to trackers.
         *
         * @returns {Void}
         */
        trackConfiguration() {
            if (!Boolean(Number(this.isAnalyticsEnabled))) return;
            // Only track in production environment.
            if (checkCCM19Consent() || checkOneTrustConsent()) {
                // TODO: Remove or uncomment later
                // fbq('trackCustom', 'CompleteConfiguration', {
                //     car_type: this.model,
                // });

                // Azerion tracking for NL.
                if (this.siteHandle == 'mgMotorsNetherlands') {
                    this.$root.insertTrackingImageAzerion(
                        'https://p1.zemanta.com/p/15834/10264',
                    );

                    const sas_tmstp = Math.round(Math.random() * 10000000000);
                    const showHeroesUrl =
                        'https://www8.smartadserver.com/h/tp?advid=450139&trcid=31688&val=&transid=&ref=&uid=&tmstp=' +
                        sas_tmstp +
                        '';
                    this.$root.insertTrackingImageShowHeroes(showHeroesUrl);
                }

                // Floodlight tracking for NL, LU, BE.
                if (
                    this.siteHandle == 'mgMotorsNetherlands' ||
                    this.siteHandle == 'mgMotorsLuxembourgFr' ||
                    this.siteHandle == 'mgMotorsBelgiumNl' ||
                    this.siteHandle == 'mgMotorsBelgiumFr'
                ) {
                    const axel = Math.random() + '';
                    const a = axel * 10000000000000;
                    const iframe = document.createElement('iframe');
                    const url =
                        'https://10256359.fls.doubleclick.net/activityi;src=10256359;type=mg_co0;cat=mg_le02;u1=' +
                        this.siteHandle +
                        ';u2=' +
                        this.$root.languageCode +
                        ';u3=' +
                        window.location +
                        ';u4=;u8=;u9=zs-ev;u10=' +
                        this.configuratorResult.color.key +
                        ';u11=' +
                        this.configuratorResult.type.key +
                        ';dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ord=' +
                        a +
                        '?';
                    iframe.setAttribute('src', url);
                    iframe.setAttribute('width', 1);
                    iframe.setAttribute('height', '1');
                    iframe.setAttribute('frameborder', 0);
                    iframe.style.display = 'none';
                    document.body.appendChild(iframe);
                }

                // Google tracking for FR.
                if (this.siteHandle == 'mgMotorsFrance') {
                    gtag('event', 'conversion', {
                        allow_custom_scripts: true,
                        u1: 'zsev',
                        send_to: 'DC-10256326/mg-autos/fr_in004+unique',
                    }); // france - FR_INI_Counter_Offer
                }

                // Google tracking for NO.
                if (this.$root.handle == 'mgMotorsNorway') {
                    gtag('event', 'conversion', {
                        allow_custom_scripts: true,
                        send_to: 'DC-10210913/conve0/no_le001+standard',
                    }); // Norway - NO_INI_Lead_Offer
                }

                // Platform 161 tracking for NL / NO.
                if (typeof p161 === 'object') {
                    let pixelId = '';
                    if (this.siteHandle === 'mgMotorsNetherlands') {
                        pixelId = '3161294';
                    }
                    if (this.siteHandle === 'mgMotorsNorway') {
                        pixelId = '3161323';
                    }
                    if (pixelId !== '') {
                        p161.track(pixelId, {
                            customer_extra:
                                this.model +
                                '_' +
                                this.configuratorResult.color.key +
                                '_' +
                                this.configuratorResult.type.key,
                        });
                    }
                }
            }
        },

        handleSantanderModalVisibility() {
            EventBus.$emit('handle-santander-modal-visibility');
        },

        handleSCFModalVisibility(scfHandle = null) {
            EventBus.$emit('handle-scf-modal-visibility', scfHandle);
        },

        getMaterialCodeFromEdition(edition, skip = false) {
            if (this.scfCalculations.materialCode && skip) {
                return this.scfCalculations.materialCode;
            }

            const materialCode = Object.keys(edition).find((key) =>
                key.includes('encodedMaterialCode'),
            );
            const isMaterialCodeEmpty =
                !materialCode || !Boolean(materialCode.length);
            if (isMaterialCodeEmpty) return null;
            return edition[materialCode];
        },

        initSpecsClickHandler() {
            setTimeout(() => {
                document
                    .querySelector('a[href="#specification"]')
                    .addEventListener('click', this.toggleSeeFullSpecs);
            }, 1000);
        },

        toggleSeeFullSpecs(e) {
            e.preventDefault();
            this.chapterOpen = !this.chapterOpen;
            EventBus.$emit('openSpecs');
            // reset scrolltrigger when section opens/closes
            ScrollTrigger.refresh();
            if (this.chapterOpen) {
                gsap.fromTo(
                    this.$el.querySelector('.section-chapter'),
                    { height: 0 },
                    { height: 'auto', duration: 0.6 },
                );
                this.scrollToElement = e.currentTarget;
                zenscroll.to(this.$el.querySelector('.section-chapter'));
                // datalayer
                if (this.$root.handle == 'mgMotorsFrance') {
                    dataLayer.push({
                        event: 'contentClic',
                        eventCategory: 'Showroom Content',
                        eventAction: e.currentTarget.dataset.section,
                        eventLabel: e.currentTarget.dataset.model,
                    });
                }
            } else {
                gsap.fromTo(
                    this.$el.querySelector('.section-chapter'),
                    { height: 'auto' },
                    { height: 0, duration: 0.6, delay: 0.4 },
                );
                zenscroll.to(this.$el, 700);
                if (checkCCM19Consent() || checkOneTrustConsent()) {
                    // check if you are on production-enviroment
                    if (this.$root.handle == 'mgMotorsFrance') {
                        gtag('event', 'conversion', {
                            allow_custom_scripts: true,
                            send_to: 'DC-10256326/mg-autos/fr_in002+unique',
                        }); //french
                    }
                    if (this.$root.handle == 'mgMotorsNorway') {
                        gtag('event', 'conversion', {
                            allow_custom_scripts: true,
                            send_to: 'DC-10210913/retar0/no_re001+standard',
                        }); //norway
                    }
                }
            }
        },
    },
});
